import { useCallback } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { DateTime } from 'luxon';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledDateField } from '@/components/inputFields/ControlledDateField';
import { ControlledRadioField } from '@/components/inputFields/ControlledRadioField';
import { ControlledTextOrNumberField } from '@/components/inputFields/ControlledTextOrNumberField';
import {
  useRegisterWidgetSitePanelSave,
  useWidgetSitePanelCtx,
  useWidgetSiteTabbedShellCtx,
} from '@/components/widgetConfigSiteTabbedShell/index.ts';
import { useLayoutProps } from '@/hooks';
import { fromApiDateTime, toApiDateTime } from '@/utils';

import {
  EventTableConfig,
  EventTableInterval,
  EventTableType,
  tableTypeValueToKey,
} from '../../types.ts';
import { tableIntervalValueToKey } from '../tableIntervalValueToKey.ts';

type EventTableConfigFormProps = {
  defaultConfig: EventTableConfig;
};

export const EventTableConfigForm = ({ defaultConfig }: EventTableConfigFormProps) => {
  const { t } = useTranslation(['common', 'eventStream']);
  const { onSave } = useWidgetSitePanelCtx();
  const { shellWidgetId } = useWidgetSiteTabbedShellCtx();
  const { config, update: updateEventTableConfig } = useLayoutProps<EventTableConfig>({
    widgetId: shellWidgetId,
    defaultConfig: { ...defaultConfig, title: t('eventStream:eventStream') },
  });
  const { control, getValues } = useForm<EventTableConfig>({
    defaultValues: config,
  });

  const fromWatched = useWatch({ control, name: 'from' });
  const liveIntervalWatched = useWatch({ control, name: 'liveInterval' });
  const titleWatched = useWatch({ control, name: 'title' });
  const toWatched = useWatch({ control, name: 'to' });
  const typeWatched = useWatch({ control, name: 'type' });

  const getNewConfig = useCallback((): EventTableConfig => {
    const currentValues = getValues();
    const fromDate = getValidDate(fromWatched);
    const formattedFrom = toApiDateTime(fromDate);
    const toDate = getValidDate(toWatched);
    const formattedTo = toApiDateTime(toDate);

    return {
      ...currentValues,
      from: formattedFrom,
      liveInterval: liveIntervalWatched,
      title: titleWatched,
      to: formattedTo,
      type: typeWatched,
    };
  }, [fromWatched, getValues, liveIntervalWatched, titleWatched, toWatched, typeWatched]);

  const save = () => {
    const newConfig = getNewConfig();
    updateEventTableConfig({ widgetId: shellWidgetId, config: newConfig });
    onSave(newConfig);
  };

  useRegisterWidgetSitePanelSave(save);

  return (
    <FlexAligner direction='column' gap='1.25rem' fullHeight>
      <ControlledTextOrNumberField control={control} name='title' label={t('title')} />
      <ControlledRadioField
        control={control}
        name='type'
        options={Object.values(EventTableType).map(v => ({
          value: v,
          label: t(`eventStream:tableType.${tableTypeValueToKey[v]}`),
        }))}
      />
      <ControlledDateField
        render={typeWatched === EventTableType.HISTORICAL}
        control={control}
        name='from'
        label={t('from')}
      />
      <ControlledDateField
        render={typeWatched === EventTableType.HISTORICAL}
        control={control}
        name='to'
        label={t('to')}
      />
      <ControlledRadioField
        render={typeWatched === EventTableType.LIVE}
        control={control}
        name='liveInterval'
        options={Object.values(EventTableInterval).map(v => ({
          value: v,
          label: t(`eventStream:tableInterval.${tableIntervalValueToKey[v]}`),
        }))}
      />
    </FlexAligner>
  );
};

const getValidDate = (val: unknown): DateTime =>
  typeof val === 'object' && val instanceof DateTime ? val : fromApiDateTime(String(val));
